import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../i18n";
import { api } from "../services/api";
import "./Pricing.css";

export default function Pricing() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [error, setError] = useState(null);

  const PLAN_KEYS = ["free", "starter", "pro"];

  const plans = [
    {
      key: "free",
      name: t("pricing.freeName"),
      price: t("pricing.freePrice"),
      period: t("pricing.freePeriod"),
      desc: t("pricing.freeDesc"),
      badges: [t("pricing.freeBadge1"), t("pricing.freeBadge2")],
      features: [
        t("pricing.feat1Preset"), t("pricing.featAiSummaries"), t("pricing.featAiChat"),
      ],
      excluded: [t("pricing.featNoChannels"), t("pricing.featNoWhatsapp"), t("pricing.featNoNotion")],
      cta: t("pricing.freeCta"),
      popular: false,
    },
    {
      key: "starter",
      name: t("pricing.starterName"),
      price: t("pricing.starterPrice"),
      period: t("pricing.starterPeriod"),
      desc: t("pricing.starterDesc"),
      badges: [t("pricing.starterBadge1"), t("pricing.starterBadge2")],
      features: [
        t("pricing.feat4Channels"), t("pricing.featAiSummaries"), t("pricing.featAiChat"),
        t("pricing.featSchedule"),
      ],
      excluded: [t("pricing.featNoPresets"), t("pricing.featNoNotion")],
      cta: t("pricing.starterCta"),
      popular: true,
    },
    {
      key: "pro",
      name: t("pricing.proName"),
      price: t("pricing.proPrice"),
      period: t("pricing.proPeriod"),
      desc: t("pricing.proDesc"),
      badges: [t("pricing.proBadge1"), t("pricing.proBadge2")],
      features: [
        t("pricing.feat6Channels"), t("pricing.featAllPresets"), t("pricing.featAiSummaries"),
        t("pricing.featAiChat"), t("pricing.featSchedule"), t("pricing.featNotion"),
      ],
      excluded: [],
      cta: t("pricing.proCta"),
      popular: false,
    },
  ];

  const faq = [
    { q: t("pricing.faq1Q"), a: t("pricing.faq1A") },
    { q: t("pricing.faq2Q"), a: t("pricing.faq2A") },
    { q: t("pricing.faq3Q"), a: t("pricing.faq3A") },
    { q: t("pricing.faq4Q"), a: t("pricing.faq4A") },
    { q: t("pricing.faq5Q"), a: t("pricing.faq5A") },
  ];

  const handleUpgrade = async (planKey) => {
    setLoadingPlan(planKey);
    setError(null);
    try {
      const { checkout_url } = await api.createCheckout(planKey);
      if (checkout_url.startsWith("http") && !checkout_url.includes(window.location.host)) {
        window.location.href = checkout_url;
      } else {
        const path = checkout_url.startsWith("http")
          ? new URL(checkout_url).pathname + new URL(checkout_url).search
          : checkout_url;
        navigate(path);
      }
    } catch (err) {
      setLoadingPlan(null);
      if (err.status === 400) {
        setError(err.message);
      } else {
        setError(t("subscription.errorGeneric"));
      }
    }
  };

  const isCurrentPlan = (planKey) => user?.plan === planKey;
  const canUpgrade = (planKey) => {
    if (!user) return false;
    const currentIdx = PLAN_KEYS.indexOf(user.plan);
    const targetIdx = PLAN_KEYS.indexOf(planKey);
    return targetIdx > currentIdx;
  };

  return (
    <div className="pricing-page">
      <section className="pricing-hero">
        <div className="pricing-hero-bg" />
        <div className="pricing-hero-content">
          <span className="section-eyebrow">{t("pricing.eyebrow")}</span>
          <h1 className="pricing-title">
            {t("pricing.title1")}<br />
            <em>{t("pricing.title2")}</em>
          </h1>
          <p className="pricing-subtitle">{t("pricing.subtitle")}</p>
        </div>
      </section>

      {error && (
        <div className="pricing-error">
          <span>{error}</span>
          <button className="toast-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      <section className="plans-section">
        <div className="plans-grid">
          {plans.map((plan) => (
            <div key={plan.key} className={`plan-card ${plan.popular ? "plan-popular" : ""}`}>
              {plan.popular && <div className="plan-badge">{t("pricing.mostPopular")}</div>}
              <div className="plan-header">
                <h3 className={`plan-name ${plan.popular ? "plan-name-accent" : ""}`}>{plan.name}</h3>
                <div className="plan-price">
                  <span className="price-amount">{plan.price}</span>
                  <span className="price-period">{plan.period}</span>
                </div>
                <p className="plan-desc">{plan.desc}</p>
              </div>

              <div className="plan-badges">
                {plan.badges.map((badge) => (
                  <span key={badge} className={`plan-badge-pill ${plan.popular ? "badge-accent" : ""}`}>
                    {badge}
                  </span>
                ))}
              </div>

              <div className="plan-features">
                {plan.features.map((f) => (
                  <div key={f} className="plan-feature">
                    <span className="feature-check">✓</span>
                    {f}
                  </div>
                ))}
                {plan.excluded.map((f) => (
                  <div key={f} className="plan-feature excluded">
                    <span className="feature-x">—</span>
                    {f}
                  </div>
                ))}
              </div>

              {user && isCurrentPlan(plan.key) ? (
                <button className="plan-cta plan-cta-current" disabled>
                  {t("subscription.currentPlan")}
                </button>
              ) : user && canUpgrade(plan.key) ? (
                <button
                  className={`plan-cta ${plan.popular ? "plan-cta-primary" : ""}`}
                  onClick={() => handleUpgrade(plan.key)}
                  disabled={loadingPlan !== null}
                >
                  {loadingPlan === plan.key ? (
                    <span className="btn-spinner" />
                  ) : (
                    <>
                      {t("subscription.upgrade")}
                      <span className="btn-arrow">→</span>
                    </>
                  )}
                </button>
              ) : (
                <Link
                  to={user ? "/dashboard" : "/login"}
                  className={`plan-cta ${plan.popular ? "plan-cta-primary" : ""}`}
                >
                  {plan.cta}
                  <span className="btn-arrow">→</span>
                </Link>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="faq-section">
        <div className="faq-inner">
          <h2 className="faq-title">{t("pricing.faqTitle")}</h2>
          <div className="faq-grid">
            {faq.map((item) => (
              <div key={item.q} className="faq-item">
                <h4 className="faq-question">{item.q}</h4>
                <p className="faq-answer">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
